import styled, { createGlobalStyle } from 'styled-components'

/**
 * Global print styles - minimal overrides to preserve colors
 * NOTE: Colors are preserved for print. Only box-shadows are removed for cleaner printing.
 */
export const PrintStyles = createGlobalStyle`
  @media print {
    /* Remove box shadows for cleaner printing */
    * {
      box-shadow: none !important;
    }
    
    /* Colors are preserved - no color overrides */
  }
`

/**
 * Section Card Component
 * Provides colored sections in UI for better readability
 * Automatically converts to black and white when printing
 */

// Color scheme for different section types
const sectionColors = {
  screeningQuestions: {
    bg: 'rgba(103, 121, 227, 0.08)', // Light blue
    border: 'rgba(103, 121, 227, 0.2)',
    headerBg: 'rgba(103, 121, 227, 0.12)',
    headerText: '#4338ca',
  },
  presentingComplaint: {
    bg: 'rgba(239, 68, 68, 0.08)', // Light red
    border: 'rgba(239, 68, 68, 0.2)',
    headerBg: 'rgba(239, 68, 68, 0.12)',
    headerText: '#dc2626',
  },
  observations: {
    bg: 'rgba(245, 158, 11, 0.08)', // Light orange
    border: 'rgba(245, 158, 11, 0.2)',
    headerBg: 'rgba(245, 158, 11, 0.12)',
    headerText: '#d97706',
  },
  primaryClinicalDiagnosis: {
    bg: 'rgba(16, 185, 129, 0.08)', // Light green
    border: 'rgba(16, 185, 129, 0.2)',
    headerBg: 'rgba(16, 185, 129, 0.12)',
    headerText: '#059669',
  },
  differentialClinicalDiagnoses: {
    bg: 'rgba(16, 185, 129, 0.06)', // Lighter green
    border: 'rgba(16, 185, 129, 0.15)',
    headerBg: 'rgba(16, 185, 129, 0.1)',
    headerText: '#047857',
  },
  diagnosis: {
    bg: 'rgba(139, 92, 246, 0.08)', // Light purple
    border: 'rgba(139, 92, 246, 0.2)',
    headerBg: 'rgba(139, 92, 246, 0.12)',
    headerText: '#7c3aed',
  },
  advice: {
    bg: 'rgba(59, 130, 246, 0.08)', // Light blue
    border: 'rgba(59, 130, 246, 0.2)',
    headerBg: 'rgba(59, 130, 246, 0.12)',
    headerText: '#2563eb',
  },
  notes: {
    bg: 'rgba(168, 85, 247, 0.08)', // Light purple
    border: 'rgba(168, 85, 247, 0.2)',
    headerBg: 'rgba(168, 85, 247, 0.12)',
    headerText: '#9333ea',
  },
  emrSection: {
    bg: 'rgba(148, 163, 184, 0.08)', // Light gray
    border: 'rgba(148, 163, 184, 0.2)',
    headerBg: 'rgba(148, 163, 184, 0.12)',
    headerText: '#475569',
  },
  default: {
    bg: 'rgba(241, 245, 249, 0.5)', // Very light gray
    border: 'rgba(203, 213, 225, 0.3)',
    headerBg: 'rgba(241, 245, 249, 0.8)',
    headerText: '#334155',
  },
}

export const SectionCard = styled.div`
  background: ${(props) => sectionColors[props.$sectionType]?.bg || sectionColors.default.bg};
  border: 2px solid ${(props) => sectionColors[props.$sectionType]?.border || sectionColors.default.border};
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease;
  
  /* Print styles - preserve colors */
  @media print {
    box-shadow: none !important;
    page-break-inside: avoid;
    margin-bottom: 16px;
    padding: 16px;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  
  /* Screen only - hover effect */
  @media screen {
    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
      border-color: ${(props) => sectionColors[props.$sectionType]?.border || sectionColors.default.border};
    }
  }
`

export const SectionHeader = styled.div`
  background: ${(props) => sectionColors[props.$sectionType]?.headerBg || sectionColors.default.headerBg};
  color: ${(props) => sectionColors[props.$sectionType]?.headerText || sectionColors.default.headerText};
  font-weight: 700;
  font-size: 16px;
  padding: 12px 16px;
  margin: -20px -20px 16px -20px;
  border-radius: 12px 12px 0 0;
  border-bottom: 2px solid ${(props) => sectionColors[props.$sectionType]?.border || sectionColors.default.border};
  display: flex;
  align-items: center;
  gap: 8px;
  
  /* Print styles - preserve colors */
  @media print {
    margin: -16px -16px 12px -16px;
    padding: 10px 14px;
    font-size: 15px;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  
  /* Screen only */
  @media screen {
    &::before {
      content: '';
      width: 4px;
      height: 20px;
      background: ${(props) => sectionColors[props.$sectionType]?.headerText || sectionColors.default.headerText};
      border-radius: 2px;
    }
  }
  
  @media print {
    &::before {
      display: none;
    }
  }
`

export const SectionContent = styled.div`
  color: #1e293b;
  line-height: 1.6;
`

export const SubSectionCard = styled.div`
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid ${(props) => sectionColors[props.$sectionType]?.border || 'rgba(203, 213, 225, 0.3)'};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  
  @media print {
    padding: 12px;
    margin-bottom: 10px;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`

export const DetailRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 8px;
  font-size: 14px;
  color: #334155;
  
  @media print {
    gap: 12px;
  }
`

export const DetailLabel = styled.strong`
  color: #475569;
  font-weight: 600;
`

